import {
  NotificationToolTipFeature,
  NotificationToolTipStandard
} from '@/components/tooltip/notification.tooltip';
import CustomLabel from '@/components/custom-label';
import {
  getReceiverOptions,
  convertFrequencyTimesToOptions,
  convertLinkFunctionsToOptions,
  convertWeekDaysToOptions,
  convertTargetGroupsToOptions
} from '@/constants/notification.constant';
import { NotificationPermissionMode } from '@/enums/mode.enum';
import { useQuery } from '@/hooks/query.hook';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { watchUser } from '@/redux/slices/auth.slice';
import {
  createNotification,
  getNotificationDetail,
  resetNotificationDetail,
  updateNotification,
  watchDetailLoading,
  watchLoadingNotification,
  watchNotificationDetail,
  getFrequencyTimes,
  getLinkFunctions,
  getWeekDays,
  getTargetGroups,
  watchFrequencyTimes,
  watchLinkFunctions,
  watchWeekDays,
  watchTargetGroups
} from '@/redux/slices/notification.slice';
import { convertFileToBase64 } from '@/utils/common';
import {
  ArrowLeftOutlined,
  CaretRightOutlined,
  CloseCircleOutlined,
  InfoCircleFilled,
  InfoCircleOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { css } from '@emotion/react';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Switch,
  Tooltip,
  Typography,
  Upload,
  App
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

const { TextArea } = Input;

export default function NotificationCreateWebApp() {
  const { modal } = App.useApp();
  const [form] = Form.useForm();
  const [targetGroupValue, setTargetGroupValue] = useState<number[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const user = useAppSelector(watchUser);

  const dispatch = useAppDispatch();
  const { mode, id } = useQuery();

  const notificationDetail = useAppSelector(watchNotificationDetail);
  const loading = useAppSelector(watchDetailLoading);
  const loadingNotification = useAppSelector(watchLoadingNotification);

  const isEditMode = mode === NotificationPermissionMode.EDITWEBAPP && id;
  const notificationId = id ? Number(id) : null;

  const frequencyTimes = useAppSelector(watchFrequencyTimes);
  const linkFunctions = useAppSelector(watchLinkFunctions);
  const weekDays = useAppSelector(watchWeekDays);
  const targetGroups = useAppSelector(watchTargetGroups);

  const frequencyOptions = convertFrequencyTimesToOptions(frequencyTimes);
  const weekDaysOptions = convertWeekDaysToOptions(weekDays);
  const receiverOptions = getReceiverOptions();
  const linkFunctionOptions = convertLinkFunctionsToOptions(linkFunctions);
  const targetGroupOptions = convertTargetGroupsToOptions(targetGroups);

  const handleGoBack = () => {
    window.history.back();
  };

  const showCancelConfirmModal = () => {
    modal.confirm({
      icon: (
        <InfoCircleFilled
          style={{
            color: '#FAAD14'
          }}
        />
      ),
      title: 'Bạn có chắc chắn xóa thông báo này không?',
      content: (
        <em>
          Khi hủy thông báo này thì toàn bộ dữ liệu đã tạo sẽ bị xóa.
        </em>
      ),
      okText: 'Xóa',
      cancelText: 'Hủy',
      centered: true,
      className: 'custom-modal-center-button',
      onOk: () => {
        handleGoBack();
      },
      onCancel: () => {
      }
    });
  };

  const showLinkFunctionWarning = () => {
    modal.info({
      icon: (
        <InfoCircleFilled
          style={{
            color: '#FAAD14'
          }}
        />
      ),
      title: 'Bạn chưa chọn chức năng liên kết!',
      content:
        'Khi Chức năng liên kết được bật thì bắt buộc phải chọn trang điều hướng cho chức năng liên kết!',
      okText: 'Đã hiểu',
      centered: true,
      className: 'custom-modal-center-button'
    });
  };

  const showImageFeatureWarning = () => {
    modal.info({
      icon: (
        <InfoCircleFilled
          style={{
            color: '#FAAD14'
          }}
        />
      ),
      title: 'Bạn chưa tải lên ảnh!',
      content:
        'Đối với kiểu hiển thị "Nổi bật", vui lòng tải ảnh lên để lưu thông báo!',
      okText: 'Đã hiểu',
      centered: true,
      className: 'custom-modal-center-button'
    });
  };

  const handleSubmit = (values: any) => {
    if (values.linkFunction && !values.redirectUrl) {
      showLinkFunctionWarning();
      return;
    }

    if (values.imageDisplayType === 'FEATURED' && !fileList.length) {
      showImageFeatureWarning();
      return;
    }

    if (!user?.id) return;

    const sendDateTime =
      values.sendDate && values.sendTime
        ? dayjs(values.sendDate)
            .hour(values.sendTime.hour())
            .minute(values.sendTime.minute())
            .second(0)
            .format('YYYY-MM-DD HH:mm:ss')
        : '';

    const basePayload: CreateNotificationPayload = {
      TITLE: values.title,
      CONTENT: values.content,
      TARGET_TYPE: values.recipients,
      TARGET_GROUP: values.targetGroup || [],
      FREQUENCY_TIME_ID: values.isLoop ? values.repeatFrequency : null,
      IS_REPEAT: values.isLoop ? true : false,
      WEEK_DAY_IDS: values.repeatWeekDays || [],
      IMAGE: fileList[0]?.response?.data || fileList[0]?.url || null,
      IMAGE_TYPE: values.imageDisplayType || 'STANDARD',
      LINK_ENABLED: values.linkFunction ? true : false,
      LINK_FUNCTION_ID: values.redirectUrl || null,
      EXPIRED_AT: values.expiredAt
        ? values.expiredAt.format('YYYY-MM-DD HH:mm:ss')
        : null,
      SEND_DATE: sendDateTime,
      CREATED_BY: user.id
    };

    if (isEditMode && notificationId) {
      const updatePayload: UpdateNotificationPayload = {
        ...basePayload,
        ID: notificationId
      };
      dispatch(updateNotification(updatePayload)).then(() => {
        handleGoBack();
      });
    } else {
      dispatch(createNotification(basePayload)).then(() => {
        handleGoBack();
      });
    }
  };

  const customRequest = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;

    try {
      setUploading(true);

      const base64 = await convertFileToBase64(file);

      if (!base64) {
        throw new Error('Failed to convert file to Base64');
      }

      const response = await apiFetcher.post(
        'upload-image',
        {
          thumbnail: base64
        },
        {
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress({ percent });
            }
          }
        }
      );

      setUploading(false);

      const newFileList = [
        {
          uid: file.uid,
          name: file.name,
          status: 'done',
          url: response.data
        }
      ];

      setFileList(newFileList);

      onSuccess(response, file);
    } catch (error: any) {
      setUploading(false);
      setFileList([]);
      onError(error);
    }
  };

  useEffect(() => {
    if (frequencyTimes.length === 0) {
      dispatch(getFrequencyTimes());
    }
    if (linkFunctions.length === 0) {
      dispatch(getLinkFunctions());
    }
    if (weekDays.length === 0) {
      dispatch(getWeekDays());
    }
    if (targetGroups.length === 0) {
      dispatch(getTargetGroups());
    }
  }, [
    dispatch,
    frequencyTimes.length,
    linkFunctions.length,
    weekDays.length,
    targetGroups.length
  ]);

  useEffect(() => {
    if (isEditMode && notificationId) {
      dispatch(getNotificationDetail(notificationId));
    }
  }, [dispatch, isEditMode, notificationId]);

  useEffect(() => {
    if (notificationDetail && isEditMode) {
      const sendDate = notificationDetail.SEND_DATE
        ? dayjs(notificationDetail.SEND_DATE)
        : null;

      form.setFieldsValue({
        title: notificationDetail.TITLE,
        content: notificationDetail.CONTENT,
        sendDate: sendDate,
        sendTime: sendDate,
        isLoop: notificationDetail.IS_REPEAT === 1,
        repeatFrequency: notificationDetail.FREQUENCY_TIME_ID,
        expiredAt: notificationDetail.EXPIRED_AT
          ? dayjs(notificationDetail.EXPIRED_AT)
          : null,
        repeatWeekDays: notificationDetail.WEEK_DAYS
          ? notificationDetail.WEEK_DAYS.map((day) => day.ID)
          : [],
        image: notificationDetail.IMAGE || null,
        recipients: notificationDetail.TARGET_TYPE,
        targetGroup: notificationDetail.TARGET_GROUP
          ? notificationDetail.TARGET_GROUP.map((group) => group.ID)
          : [],
        imageDisplayType: notificationDetail.IMAGE_TYPE || 'STANDARD',
        linkFunction: notificationDetail.LINK_ENABLED === 1,
        redirectUrl: notificationDetail.LINK_FUNCTION_ID
      });

      setTargetGroupValue(
        notificationDetail.TARGET_GROUP?.map((group) => group.ID) || []
      );

      if (notificationDetail.IMAGE) {
        setFileList([
          {
            uid: '-1',
            name: 'image.jpg',
            status: 'done',
            url: notificationDetail.IMAGE
          }
        ]);
      }
    }
  }, [notificationDetail, isEditMode, form]);

  useEffect(() => {
    return () => {
      form.resetFields();
      setTargetGroupValue([]);
      setFileList([]);
      dispatch(resetNotificationDetail());
    };
  }, [form, dispatch]);

  return (
    <div css={rootStyles}>
      <style>
        {`
          .custom-tooltip-notification .ant-tooltip-inner {
            background-color: #ffffff !important;
            color: #000000 !important;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
            border-radius: 6px !important;
            padding: 12px !important;
          }

          .custom-tooltip-notification .ant-tooltip-arrow::before {
            background-color: #ffffff !important;
          }

          .custom-tooltip-notification {
            max-width: 320px;
          }

          .custom-modal-center-button .ant-modal-confirm-btns {
            display: flex !important;
            justify-content: center !important;
            gap: 1rem !important;
          }

          .custom-modal-center-button .ant-btn-primary {
            margin-left: 0 !important;
          }

          .custom-modal-center-button .ant-modal-confirm-content {
            font-style: italic !important;
          }

          .no-preview-upload .ant-upload-list-item-thumbnail,
          .no-preview-upload .ant-upload-list-item-name {
            pointer-events: none;
            cursor: default;
          }
        `}
      </style>
      <Row css={boxStyles} justify='space-between'>
        <Typography.Title level={5} style={{ marginBottom: 0, fontWeight: 700 }}>
          {isEditMode ? 'Cập nhật thông báo' : 'Tạo mới thông báo'} Web/App
        </Typography.Title>

        <Button
          type='text'
          icon={<ArrowLeftOutlined />}
          onClick={handleGoBack}
        />
      </Row>

      <Form
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        css={formStyles}
      >
        {/* Thời gian gửi Section */}
        <div css={sectionStyles}>
          <Row gutter={[8, 0]} align='bottom'>
            <Col span={4}>
              {CustomLabel({ text: 'Thời gian gửi', isRequired: true })}
            </Col>
            <Col span={5}>
              <Form.Item
                name='sendDate'
                rules={[
                  {
                    required: true,
                    message: 'Vui lòng chọn ngày gửi thông báo'
                  },
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }
                      const today = dayjs().startOf('day');
                      const selectedDate = dayjs(value).startOf('day');

                      if (selectedDate.isBefore(today)) {
                        return Promise.reject(
                          new Error('Ngày gửi không được là ngày trong quá khứ')
                        );
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
                style={{ marginBottom: 0 }}
              >
                <DatePicker
                  placeholder='Chọn ngày gửi thông báo'
                  format={'DD/MM/YYYY'}
                  style={{ width: '100%' }}
                  disabledDate={(current) => {
                    return current && current < dayjs().startOf('day');
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                style={{ marginBottom: 0 }}
                shouldUpdate={(prev, cur) => prev.sendDate !== cur.sendDate}
              >
                {({ getFieldValue }) => {
                  const sendDate = getFieldValue('sendDate');

                  return (
                    <Form.Item
                      name='sendTime'
                      dependencies={['sendDate']}
                      style={{ marginBottom: 0 }}
                      rules={[
                        {
                          required: true,
                          message: 'Vui lòng chọn giờ gửi thông báo'
                        },
                        ({ getFieldValue }) => ({
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();

                            const sendDate = getFieldValue('sendDate');
                            if (!sendDate) return Promise.resolve();

                            const today = dayjs().startOf('day');
                            const selectedDate = dayjs(sendDate).startOf('day');

                            if (selectedDate.isSame(today)) {
                              const currentTime = dayjs();
                              const selectedDateTime = dayjs(sendDate)
                                .hour(value.hour())
                                .minute(value.minute());
                              if (selectedDateTime.isBefore(currentTime)) {
                                return Promise.reject(
                                  new Error(
                                    'Giờ gửi không được là giờ trong quá khứ'
                                  )
                                );
                              }
                            }

                            return Promise.resolve();
                          }
                        })
                      ]}
                    >
                      <DatePicker.TimePicker
                        key={
                          sendDate ? sendDate.format('YYYY-MM-DD') : 'default'
                        }
                        placeholder='Chọn giờ gửi thông báo'
                        format='HH:mm'
                        style={{ width: '100%' }}
                        disabledTime={() => {
                          if (!sendDate) return {};

                          const today = dayjs().startOf('day');
                          const selectedDate = dayjs(sendDate).startOf('day');

                          if (selectedDate.isSame(today)) {
                            const currentHour = dayjs().hour();
                            const currentMinute = dayjs().minute();

                            return {
                              disabledHours: () =>
                                Array.from(
                                  { length: currentHour },
                                  (_, i) => i
                                ),
                              disabledMinutes: (hour: number) =>
                                hour === currentHour
                                  ? Array.from(
                                      { length: currentMinute + 1 },
                                      (_, i) => i
                                    )
                                  : []
                            };
                          }

                          return {};
                        }}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
            <Col span={2}>
              <Form.Item
                name='isLoop'
                valuePropName='checked'
                style={{ marginBottom: 0 }}
              >
                <Checkbox>Lặp lại</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => prev.isLoop !== cur.isLoop}
        >
          {({ getFieldValue }) => {
            const isLoop = getFieldValue('isLoop');

            if (!isLoop) {
              return null;
            }

            return (
              <div css={sectionStyles}>
                <Row gutter={[8, 0]}>
                  <Typography.Title
                    level={5}
                    style={{ marginBottom: 0, fontWeight: 700 }}
                  >
                    Tần suất lặp lại thông báo{' '}
                  </Typography.Title>
                </Row>
                <Row gutter={[8, 0]} align='bottom'>
                  <Col span={4}>
                    <Typography.Text css={sectionTitleStyles}>
                      Tần suất lặp lại
                    </Typography.Text>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name='repeatFrequency'
                      rules={[
                        {
                          required: true,
                          message: 'Vui lòng chọn tần suất lặp lại!'
                        }
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder='Chọn tần suất lặp lại'
                        options={frequencyOptions}
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) =>
                    prev.repeatFrequency !== cur.repeatFrequency
                  }
                >
                  {({ getFieldValue }) => {
                    const frequencyId = getFieldValue('repeatFrequency');
                    if (!frequencyId) return null;

                    const frequency = frequencyTimes.find(
                      (f) => f.ID === frequencyId
                    );
                    const frequencyName = frequency?.NAME || '';

                    if (frequencyName.toLowerCase().includes('tuần'))
                      return (
                        <Row gutter={[8, 0]} align='middle'>
                          <Col span={4}>
                            <Typography.Text css={sectionTitleStyles}>
                              Lặp lại vào
                            </Typography.Text>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              required
                              rules={[
                                {
                                  required: true,
                                  message: 'Vui lòng chọn ngày lặp lại'
                                }
                              ]}
                              name='repeatWeekDays'
                              style={{ marginBottom: 0 }}
                            >
                              <Checkbox.Group options={weekDaysOptions} />
                            </Form.Item>
                          </Col>
                        </Row>
                      );
                  }}
                </Form.Item>

                <Row gutter={[8, 0]} align='bottom'>
                  <Col span={4}>
                    <Typography.Text css={sectionTitleStyles}>
                      Cho đến
                    </Typography.Text>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name='expiredAt'
                      dependencies={['sendDate']}
                      rules={[
                        {
                          required: true,
                          message: 'Vui lòng chọn ngày kết thúc'
                        },
                        ({ getFieldValue }) => ({
                          validator: (_, value) => {
                            if (!value) {
                              return Promise.resolve();
                            }

                            const sendDate = getFieldValue('sendDate');
                            if (!sendDate) {
                              return Promise.resolve();
                            }

                            const sendDateStart =
                              dayjs(sendDate).startOf('day');
                            const expiredDateStart =
                              dayjs(value).startOf('day');

                            if (
                              expiredDateStart.isSame(sendDateStart) ||
                              expiredDateStart.isBefore(sendDateStart)
                            ) {
                              return Promise.reject(
                                new Error('Ngày kết thúc phải lớn hơn ngày gửi')
                              );
                            }

                            return Promise.resolve();
                          }
                        })
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <DatePicker
                        placeholder='Chọn ngày kết thúc thông báo'
                        format={'DD/MM/YYYY'}
                        style={{ width: '100%' }}
                        disabledDate={(current) => {
                          const sendDate = form.getFieldValue('sendDate');
                          if (!sendDate) {
                            return current && current < dayjs().startOf('day');
                          }
                          return (
                            current && current < dayjs(sendDate).endOf('day')
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            );
          }}
        </Form.Item>

        {/* Nội dung thông báo Section */}
        <div css={sectionStyles}>
          <Typography.Title
            level={5}
            style={{ marginBottom: 0, fontWeight: 700 }}
          >
            Nội dung thông báo
          </Typography.Title>

          <Row gutter={16}>
            <Col span={4}>
              {CustomLabel({ text: 'Tiêu đề thông báo', isRequired: true })}
            </Col>
            <Col span={10}>
              <Form.Item
                name='title'
                rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  placeholder='Nhập tiêu đề'
                  showCount
                  maxLength={95}
                  style={{ padding: '8px 12px', width: '75%' }}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={4}>
              {CustomLabel({ text: 'Nội dung thông báo', isRequired: true })}
            </Col>
            <Col span={24}>
              <Form.Item
                name='content'
                rules={[{ required: true, message: 'Vui lòng nhập nội dung' }]}
              >
                <TextArea
                  placeholder='Nhập nội dung thông báo'
                  showCount
                  maxLength={200}
                  rows={4}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
          {/* Hình ảnh đính kèm Section */}
          <Typography.Text
            css={sectionTitleStyles}
            style={{
              fontWeight: 700
            }}
          >
            Hình ảnh đính kèm
          </Typography.Text>

          <Row>
            <Col span={24}>
              <Form.Item
                label='Chọn kiểu hiển thị của hình ảnh thông báo'
                name='imageDisplayType'
                initialValue={'STANDARD'}
              >
                <Radio.Group css={radioVerticalStyles}>
                  <Radio value='STANDARD'>
                    <Typography.Text
                      style={{ fontWeight: 500, paddingRight: '2px' }}
                    >
                      Tiêu chuẩn{' '}
                    </Typography.Text>
                    <Tooltip
                      title={<NotificationToolTipStandard />}
                      placement='right'
                      overlayClassName='custom-tooltip-notification'
                    >
                      <InfoCircleOutlined
                        style={{
                          color: '#1890ff'
                        }}
                      />
                    </Tooltip>
                  </Radio>
                  <Radio value='FEATURED'>
                    <Typography.Text
                      style={{ fontWeight: 500, paddingRight: '2px' }}
                    >
                      Nổi bật{' '}
                    </Typography.Text>
                    <Tooltip
                      title={<NotificationToolTipFeature />}
                      placement='right'
                      overlayClassName='custom-tooltip-notification'
                    >
                      <InfoCircleOutlined
                        style={{
                          color: '#1890ff'
                        }}
                      />
                    </Tooltip>
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name='image'>
                <Upload
                  className='no-preview-upload'
                  customRequest={customRequest}
                  listType='picture'
                  maxCount={1}
                  fileList={fileList}
                  showUploadList={{
                    showPreviewIcon: false,
                    showRemoveIcon: true
                  }}
                  onPreview={() => {}}
                  beforeUpload={(file) => {
                    const isImage = file.type.startsWith('image/');
                    if (!isImage) {
                      return Upload.LIST_IGNORE;
                    }
                    return isImage;
                  }}
                  accept='image/*'
                  disabled={uploading}
                  onChange={(info) => {
                    if (info.file.status === 'removed') {
                      setFileList([]);
                    } else {
                      setFileList(info.fileList);
                    }
                  }}
                  onRemove={() => {
                    setFileList([]);
                  }}
                >
                  <Button
                    icon={<UploadOutlined />}
                    loading={uploading}
                    disabled={uploading}
                  >
                    {uploading ? 'Đang tải lên...' : 'Chọn để tải lên hình ảnh'}
                  </Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Danh sách người nhận Section */}
        <div css={sectionStyles}>
          <Typography.Title level={5} style={{ marginBottom: 0, fontWeight: 700 }}>
            Danh sách người nhận
          </Typography.Title>

          <Row gutter={16}>
            <Col span={3}>
              {CustomLabel({ text: 'Người nhận', isRequired: true })}
            </Col>
            <Col span={6}>
              <Form.Item
                name='recipients'
                rules={[
                  { required: true, message: 'Vui lòng chọn người nhận' }
                ]}
                initialValue={'ALL'}
              >
                <Select defaultActiveFirstOption options={receiverOptions} style={{ width: '60%' }} allowClear />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const receiverValue = getFieldValue('recipients');

              if (receiverValue === 'ALL') {
                return (
                  <div>
                    <CaretRightOutlined /> Khớp{' '}
                    <strong>tất cả người dùng</strong>
                  </div>
                );
              }

              return (
                <Form.Item
                  name='targetGroup'
                  label='Nhập các điều kiện để chọn đối tượng gửi thông báo'
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 6 }}
                >
                  <div
                    css={
                      targetGroupValue.length > 0
                        ? null
                        : ContainerSelectHealthStatus
                    }
                  >
                    <Select
                      css={selectStyleHealthStatus}
                      mode='multiple'
                      allowClear
                      options={targetGroupOptions}
                      placeholder='Chọn Trạng thái sức khỏe'
                      style={{ width: '95%' }}
                      value={targetGroupValue}
                      onChange={(value) => {
                        setTargetGroupValue(value || []);
                        form.setFieldValue('targetGroup', value || []);
                      }}
                      removeIcon={
                        <CloseCircleOutlined
                          style={{ color: '#1890FF', fontSize: '1.9rem' }}
                        />
                      }
                    />
                  </div>
                </Form.Item>
              );
            }}
          </Form.Item>
        </div>

        {/* Chức năng liên kết Section */}
        <div css={sectionStyles}>
          <Typography.Title level={5} style={{ marginBottom: 0 }}>
            Chức năng liên kết
          </Typography.Title>

          <Row gutter={16}>
            <Col span={3}>
              <Typography.Text css={sectionTitleStyles}>
                Chức năng liên kết
              </Typography.Text>
            </Col>
            <Col span={12}>
              <Form.Item name='linkFunction' valuePropName='checked'>
                <Switch
                  onChange={(checked) => {
                    if (!checked) {
                      form.setFieldValue('redirectUrl', null);
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={4}>
              <Typography.Text css={sectionTitleStyles}>
                Điều hướng khi người dùng nhấn chọn{' '}
              </Typography.Text>
            </Col>
            <Col span={6}>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const enabled = !!getFieldValue('linkFunction');
                  return (
                    <Form.Item name='redirectUrl'>
                      <Select
                        placeholder='Chọn chức năng liên kết'
                        options={linkFunctionOptions}
                        style={{ width: '60%' }}
                        allowClear
                        disabled={!enabled}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Submit Buttons */}
        <div css={submitButtonsStyles}>
          <Button
            type='default'
            onClick={isEditMode ? handleGoBack : showCancelConfirmModal}
            style={{
              width: '25%'
            }}
          >
            Hủy
          </Button>
          <Button
            type='primary'
            htmlType='submit'
            loading={loading || loadingNotification}
            style={{
              width: '25%'
            }}
          >
            {isEditMode ? 'Cập nhật' : 'Lưu'}
          </Button>
        </div>
      </Form>
    </div>
  );
}

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 1.4rem 2.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const formStyles = css`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const sectionStyles = css`
  padding: 0.4rem 0.8rem;
  background: var(--white-color);
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const sectionTitleStyles = css`
  font-weight: 400;
  font-size: 1.4rem;
  line-height: 3.2rem;
  margin-bottom: 0;
  display: flex;
  align-items: center;
  height: 32px;
  color: var(--text-primary-color);
`;

const submitButtonsStyles = css`
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding: 1.6rem 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
`;

const radioVerticalStyles = css`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-left: 2rem;
`;

const ContainerSelectHealthStatus = css`
  border: 1px solid #00000040;
  border-radius: 0.5rem;
  padding: 0.8rem 1.6rem;

  .ant-select-selection-item {
    background: #ffffff !important;
    border-radius: 0.2rem !important;
    border: 1px solid #d9d9d9 !important;

    .ant-select-selection-item-content {
    }
  }
`;

const selectStyleHealthStatus = css`
  .ant-select-selection-item {
    background: #ffffff !important;
    border-radius: 0.2rem !important;
    border: 1px solid #d9d9d9 !important;

    .ant-select-selection-item-content {
      font-size: 1.2rem !important;
    }
  }
`;
